import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormControl from '@mui/material/FormControl';
import { TextInput, NumberInput, PhoneInput, Services, Modal, BubbleSelectBlock, Text, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';

import * as notifier from '../../../../actions/notifier.js';
import { updateAccountSettings } from '../../../../actions/advisor.js';

class AccountSettings extends Component {
    state = {
        companyName: '',
        addressLineTwo: '',
        addressLineOne: '',
        city: '',
        province: '',
        postalCode: '',
        country: 'Canada',
        phone: '',
        contactEmail: '',
        authorityName: '',
        authorityEmail: '',
        asoType: '',
        isAsoDental: '',
        isAsoHealth: '',
        asoRunoffPeriod: '',
        showModal: false
    };

    async componentDidMount() {
        const { currentAccountMeta } = this.props;

        if (currentAccountMeta) {
            await this.setState({
                companyName: currentAccountMeta.company,
                addressLineTwo: currentAccountMeta.addressLineTwo,
                addressLineOne: currentAccountMeta.addressLineOne,
                city: currentAccountMeta.city,
                province: localizationMaps.fromEnglish("provinces", currentAccountMeta.province),
                postalCode: currentAccountMeta.postalCode,
                phone: currentAccountMeta.phone,
                contactEmail: currentAccountMeta.contactEmail,
                authorityName: currentAccountMeta.authorityName,
                authorityEmail: currentAccountMeta.authorityEmail,
                asoType: currentAccountMeta.asoType,
                isAsoDental: currentAccountMeta.isAsoDental,
                isAsoHealth: currentAccountMeta.isAsoHealth,
                asoRunoffPeriod: currentAccountMeta.asoRunoffPeriod
            });
        }

        document.addEventListener("keydown", this._handleKeyPress, false);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this._handleKeyPress, false);
    }

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };

    _confirm = async () => {
        const companyNameError = await Services.validateInput('companyName', this.state.companyName);
        const phoneError = await Services.validateInput('phone', this.state.phone);
        const contactEmailError = await Services.validateInput('contactEmail', this.state.contactEmail);
        const authorityError = await Services.validateInput('authority', this.state.authorityName);
        const authorityEmailError = await Services.validateInput('authorityEmail', this.state.authorityEmail);

        const addressLineOneError = await Services.validateInput('addressLineOne', this.state.addressLineOne);
        const cityError = await Services.validateInput('city', this.state.city);
        const provinceError = await Services.validateInput('province', this.state.province);
        const postalCodeError = await Services.validateInput('postalCode', this.state.postalCode);

        this.setState({
            companyNameError,
            phoneError,
            contactEmailError,
            authorityError,
            authorityEmailError,

            addressLineOneError,
            cityError,
            provinceError,
            postalCodeError
        });

        if (
            companyNameError ||
            phoneError ||
            contactEmailError ||
            authorityError ||
            authorityEmailError ||

            addressLineOneError ||
            cityError ||
            provinceError ||
            postalCodeError
        ) {
            this.props.dispatch(notifier.displayError({ verbiage: 'Please confirm that your have filled out all the details.' }));

            return false;
        }

        const {
            addressLineTwo,
            addressLineOne,
            city,
            province,
            postalCode,
            companyName,
            contactEmail,
            phone,
            authorityName,
            authorityEmail
        } = this.state;

        const body = {
            companyName: companyName.trim(),
            addressLineOne: addressLineOne.trim(),
            addressLineTwo: addressLineTwo?.trim() || null,
            city: city.trim(),
            province: localizationMaps.toEnglish('provinces', province),
            postalCode: postalCode.trim(),
            phone: phone.trim(),
            contactEmail: contactEmail.trim(),
            authorityName: authorityName.trim(),
            authorityEmail: authorityEmail.trim()
        };

        const success = await this.props.dispatch(updateAccountSettings(body, this.props.currentAccountMeta.accountID));

        if (success) {
            this.props.hideModal();
            document.removeEventListener("keydown", this._handleKeyPress, false);
        }

        return null;
    };

    _handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            return this._confirm();
        }

        return false;
    };

    /**
     * 
     * @param {*} input 
     */
    _formatPostalCode = (input) => {
        const formattedPostalCode = input
            .toUpperCase()
            .trim()
            .replace(/\s+/g, '') // Remove all spaces
            .replace(/([A-Z0-9]{3})([A-Z0-9]{3})/, '$1 $2'); // Add a space after the first 3 characters
        
        this.setState({ postalCode: formattedPostalCode });
    };

    render() {
        const {
            hideModal,
            currentAccountMeta
        } = this.props;

        const {
            addressLineTwo,
            addressLineOne,
            city,
            province,
            postalCode
        } = this.state;

        const styles = {
            row: {
                display: 'flex',
                flexDirection: 'row'
            },
            divider: {
                width: 40
            }
        };

        return (
            <Modal
                open={true}
                onClose={hideModal}
                title={ i18n.t(tokens.titles.accountDetails) }
                primaryButton={{
                    onClick: this._confirm
                }}
                loader={this.props.loader}
                medium={ true }
            >
                <Text
                    medium
                    bold
                    margin="10px 0 10px 0"
                >
                    { i18n.t(tokens.titles.basicInformation) }
                </Text>

                <TextInput
                    label={ i18n.t(tokens.labels.account.companyName) }
                    value={ this.state.companyName }
                    onBlur={ () => this._onBlur('companyName') }
                    onChange={ (value) => this._handleInputChange('companyName', value) }
                    error={ Boolean(this.state.companyNameError) }
                    helperText={ this.state.companyNameError }
                    disabled={ Boolean(currentAccountMeta.groupNum) }
                    fullWidth
                    autofocus
                />

                <TextInput
                    label={ i18n.t(tokens.labels.addressLineOne) }
                    autocomplete="shipping street-address"
                    value={ addressLineOne }
                    onBlur={ () => this._onBlur('addressLineOne') }
                    onChange={ (value) => this._handleInputChange('addressLineOne', value) }
                    error={ Boolean(this.state.addressLineOneError) }
                    helperText={ this.state.addressLineOneError }
                    fullWidth
                />

                <div style={ styles.row }>
                    <TextInput
                        label={ i18n.t(tokens.labels.addressLineTwo) }
                        value={ addressLineTwo }
                        onBlur={ () => this._onBlur('addressLineTwo') }
                        onChange={ (value) => this._handleInputChange('addressLineTwo', value) }
                        fullWidth
                        helperText={i18n.t(tokens.labels.addressLineTwoExample)}
                    />

                    <div style={ styles.divider } />

                    <TextInput
                        label={ i18n.t(tokens.labels.account.cityTown) }
                        autocomplete="shipping locality"
                        value={ city }
                        onBlur={ () => this._onBlur('city') }
                        onChange={ (value) => this._handleInputChange('city', value) }
                        error={ Boolean(this.state.cityError) }
                        helperText={ this.state.cityError }
                        fullWidth
                    />
                </div>


                <div style={ styles.row }>
                    <TextInput
                        label={ i18n.t(tokens.labels.province) }
                        autocomplete="shipping region"
                        value={ province }
                        onBlur={ () => this._onBlur('province') }
                        onChange={ (value) => this._handleInputChange('province', value) }
                        select
                        selectOptions={ Services.selectFieldOptions.provinceOptions() }
                        fullWidth
                    />

                    <div style={ styles.divider } />

                    <TextInput
                        label={ i18n.t(tokens.labels.postalCode) }
                        autocomplete="shipping postal-code"
                        value={ postalCode }
                        onBlur={(e) => this._formatPostalCode(e.target.value)}
                        onChange={(e) => {
                            // Allow typing only 6 alphanumeric characters and trim any excess
                            const postalCodeValue = e.target.value.toUpperCase().slice(0, 7);
                            this.setState({ postalCode: postalCodeValue });
                        }}
                        error={ Boolean(this.state.postalCodeError) }
                        helperText={ this.state.postalCodeError }
                        fullWidth
                    />
                </div>

                <PhoneInput
                    label={ i18n.t(tokens.labels.account.companyContactNumber) }
                    value={ this.state.phone }
                    onBlur={ () => this._onBlur('phone') }
                    onChange={ (value) => this._handleInputChange('phone', value) }
                    error={ Boolean(this.state.phoneError) }
                    helperText={ this.state.phoneError }
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.account.companyEmail) }
                    value={ this.state.contactEmail }
                    onBlur={ () => this._onBlur('contactEmail') }
                    onChange={ (value) => this._handleInputChange('contactEmail', value) }
                    error={ Boolean(this.state.contactEmailError) }
                    helperText={ this.state.contactEmailError }
                    fullWidth
                />

                <TextInput
                    label={ i18n.t(tokens.labels.account.signingAuthorityName) }
                    value={ this.state.authorityName }
                    onBlur={ () => this._onBlur('authorityName') }
                    onChange={ (value) => this._handleInputChange('authorityName', value) }
                    error={ Boolean(this.state.authorityError) }
                    helperText={ this.state.authorityError }
                    fullWidth
                />

                <TextInput 
                    label={ i18n.t(tokens.labels.account.signingAuthorityEmail) }
                    value={ this.state.authorityEmail }
                    onBlur={ () => this._onBlur('authorityEmail') }
                    onChange={ (value) => this._handleInputChange('authorityEmail', value) }
                    error={ Boolean(this.state.authorityEmailError) }
                    helperText={ this.state.authorityEmailError }
                    fullWidth
                />

                {
                    (!this.props.currentAccountMeta.isPrivateLabel && this.state.asoType !== null) &&
                    <>
                        <Text
                            medium
                            bold
                            margin="10px 0 10px 0"
                        >
                            ASO Information
                            { i18n.t(tokens.titles.asoInformation) }
                        </Text>

                        <Text
                            medium
                            light
                            margin="0 0 10px 0"
                        >
                            { i18n.t(tokens.labels.account.asoSettingsContact) }
                        </Text>

                        <NumberInput
                            label={ i18n.t(tokens.labels.account.asoRunoffPeriod) }
                            value={ this.state.asoRunoffPeriod }
                            fullWidth
                            disabled={ true }
                        />

                        <FormControl component="fieldset">
                            <Text
                                small
                                bold
                                margin="10px 0 0 0"
                            >
                                { i18n.t(tokens.labels.account.asoType) }
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: i18n.t(tokens.labels.account.budgeted),
                                    selected: this.state.asoType === 'Budgeted',
                                    field: 'Budgeted'
                                }}
                                second={{
                                    title: i18n.t(tokens.labels.account.inArrears),
                                    selected: this.state.asoType === 'Arrears',
                                    field: 'Arrears'
                                }}
                                disabled={ true }
                            />
                        </FormControl>

                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="10px 0 10px 0"
                            >
                                { i18n.t(tokens.labels.account.asoDental) }
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: i18n.t(tokens.buttons.yes),
                                    selected: Boolean(this.state.isAsoDental) === true,
                                    field: 'isAsoDentalYes'
                                }}
                                second={{
                                    title: i18n.t(tokens.buttons.no),
                                    selected: Boolean(this.state.isAsoDental) === false,
                                    field: 'isAsoDentalNo'
                                }}
                                disabled={ true }
                            />
                        </FormControl>

                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="10px 0 10px 0"
                            >
                                { i18n.t(tokens.labels.account.asoHealth) }
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: i18n.t(tokens.buttons.yes),
                                    selected: Boolean(this.state.isAsoHealth) === true,
                                    field: 'isAsoHealthYes'
                                }}
                                second={{
                                    title: i18n.t(tokens.buttons.no),
                                    selected: Boolean(this.state.isAsoHealth) === false,
                                    field: 'isAsoHealthNo'
                                }}
                                disabled={ true }
                            />
                        </FormControl>
                    </>
                }

                <Text
                    medium
                    bold
                    margin="10px 0 10px 0"
                >
                    { i18n.t(tokens.labels.representatives) }
                </Text>

                {
                    currentAccountMeta.representatives.length === 0 ?
                        <Text
                            medium
                            light
                            margin="0px 0 10px 0"
                        >
                            { i18n.t(tokens.labels.invoices.none) }
                        </Text>
                        :
                        currentAccountMeta.representatives.map((item) => (
                            <div style={{ marginTop: "10px" }}>
                                <Text
                                    medium
                                    bold
                                    margin="0px 0 0 0"
                                >
                                    { `${ item.name } (${ item.type === 'Service Representative' ? i18n.t(tokens.labels.serviceRepresentative) : i18n.t(tokens.labels.accountExecutive) })` }
                                </Text>

                                <Text
                                    medium
                                    margin="0px 0 10px 0"
                                >
                                    { item.email }
                                </Text>
                            </div>
                        ))
                }

                <span id="account-settings" />
            </Modal>
        );
    }
}

AccountSettings.propTypes = {
    currentAccountMeta: PropTypes.object,
    whitelabel: PropTypes.object
};

/**
 * @param state
 * @returns {{meta: null, loading: boolean}}
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    currentAccountMeta: state.advisor.currentAccountMeta,
    whitelabel: state.whitelabel
});

export default (connect(mapStateToProps)(AccountSettings));